import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import { ChartAreaLoader } from "../../../../components/Loading";
import { formatDateForAPI } from "../../../Rajasthan/Utility/const";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import {
  getPowerExchangeAnalysisData,
  getAvailableEntities,
  PowerExchangeAnalysisData,
  PowerExchangeAnalysisParams,
  PowerExchangeAnalysisSummary,
  AvailableEntity,
} from "../../../../Rest_api/RestApi";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import {
  renderHourTick,
  renderQuarterTick,
} from "../../../../components/recharts/components";
import { ChartTable } from "../../../../components/table/SummaryTable";
import LegendSummarySlotTable from "../../../../components/table/SummaryTableSlot";
import {
  BrushStart,
  ChartType,
  LegendKey,
} from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { SearchBox } from "../../../../components/Search";
import { ExchangeColors } from "../../Exchange3/FormatData";
import { getTimeRange } from "../../../Dashboard/Exchange3/Chart";
import { BarChart3, Wallet } from "lucide-react";
import MetricChip from "../../../../components/Metrics";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import { normalizeDisplayLabel } from "../../../../utility/displayText";

const DataTabs = ["comparative", "cumulative"];

// Helper function to format cost in crores, lakhs, and thousands
const formatCostTooltip = (value: number): string => {
  if (value === 0) return "0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "₹";

  // Crores (1 crore = 1,00,00,000)
  if (absValue >= 10000000) {
    const crores = absValue / 10000000;
    return `${sign}${crores.toFixed(2)} Cr`;
  }
  // Lakhs (1 lakh = 1,00,000)
  else if (absValue >= 100000) {
    const lakhs = absValue / 100000;
    return `${sign}${lakhs.toFixed(2)} Lakhs`;
  }
  // Thousands
  else if (absValue >= 1000) {
    const thousands = absValue / 1000;
    return `${sign}${thousands.toFixed(2)}K`;
  }
  // Less than 1000
  return `${sign}${absValue.toFixed(2)}`;
};

// Helper function to format cost for y-axis in lakhs
const formatCostAxis = (value: number): string => {
  if (value === 0) return "0";
  const lakhs = value / 100000;
  return `${lakhs.toFixed(1)}L`;
};

const chartSuffixes = [
  {
    suffix: "cost",
    displayName: "Cost",
    stackSuffix: "cost",
    chartType: ChartType.Line,
    unit: "Rs",
    yAxisId: "right",
  },
  {
    suffix: "price",
    displayName: "Price (Rs/kWh)",
    stackSuffix: "price",
    chartType: ChartType.Line,
    unit: "Rs/kWh",
    yAxisId: "right",
  },
  {
    suffix: "amount",
    displayName: "Volume",
    stackSuffix: "amount",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

// Type filters: Drawal and Injection
const typeFilters = [
  { value: "Drawal", label: "Drawal" },
  { value: "Injection", label: "Injection" },
];

// Variable filters: Only Cost and Price (Volume is always selected, not shown in filter)
const variableFilterMetrics = chartSuffixes.filter(
  (metric) => metric.suffix !== "amount",
);

let Intervals = [
  {
    label: "Time Slot",
    key: "slot" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: true,
  },
  {
    label: "Date",
    key: "day" as const,
    values: ["x_axis"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

const SLOT_INTERVAL =
  Intervals.find((interval) => interval.key === "slot") || Intervals[0];

const UNIT_OPTIONS = ["MW", "MWh"];

const exchanges = ["HPX", "IEX", "PXIL"];
const products = ["DAM", "GDAM", "HPDAM", "RTM"];

const PowerExchangeAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  // Load persisted filters from localStorage
  const loadPersistedFilters = () => {
    try {
      const persisted = localStorage.getItem("powerExchangeAnalysisFilters");
      if (persisted) {
        return JSON.parse(persisted);
      }
    } catch (error) {
      console.error("Error loading persisted filters:", error);
    }
    return null;
  };

  // Save filters to localStorage
  const savePersistedFilters = (filters: any) => {
    try {
      localStorage.setItem(
        "powerExchangeAnalysisFilters",
        JSON.stringify(filters),
      );
    } catch (error) {
      console.error("Error saving persisted filters:", error);
    }
  };

  const persistedFilters = loadPersistedFilters();

  const [activefunctionTab, setActivefunctionTab] = useState(
    persistedFilters?.activefunctionTab || DataTabs[1],
  );
  const [rawData, setRawData] = useState<PowerExchangeAnalysisData[]>([]);
  const [summaryNumbers, setSummaryNumbers] =
    useState<PowerExchangeAnalysisSummary>({
      total_drawal_cost: 0,
      total_drawal_volume_mu: 0,
      total_injection_cost: 0,
      total_injection_volume_mu: 0,
    });
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryIntervalData, setSummaryIntervalData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(
    persistedFilters?.selectedInterval || Intervals[0],
  );
  const [selectedUnit, setSelectedUnit] = useState(
    persistedFilters?.selectedUnit || UNIT_OPTIONS[0],
  );
  const [selectedBlock, setSelectedBlock] = useState<any[]>(
    persistedFilters?.selectedBlock || [],
  );
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    persistedFilters?.selectedTypes || ["Drawal", "Injection"],
  );
  // Volume is always selected (not shown in filter)
  const volumeMetric = chartSuffixes.find((m) => m.suffix === "amount");

  const [selectedVariableMetrics, setSelectedVariableMetrics] = useState<any[]>(
    [
      volumeMetric, // Volume always included
      variableFilterMetrics[0], // Cost by default (only one of Cost/Price can be selected)
    ],
  );
  const [loading, setLoading] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(
    persistedFilters?.selectedExchanges || ["IEX"],
  );
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    persistedFilters?.selectedProducts || ["DAM", "RTM"],
  );
  const [selectedEntities, setSelectedEntities] = useState<string[]>(
    persistedFilters?.selectedEntities || [],
  );
  const [availableEntities, setAvailableEntities] = useState<AvailableEntity[]>(
    [],
  );
  const dispatch = useDispatch();

  // Fetch available entities based on date range
  const fetchEntities = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const entities = await getAvailableEntities({
        start_date: startDateStr,
        end_date: endDateStr,
      });

      setAvailableEntities(entities);

      // Set default entity selection if none selected
      setSelectedEntities((prevSelected) => {
        if (prevSelected.length === 0 && entities.length > 0) {
          // Select WBSETCL by default if available, otherwise first entity
          const wbsetclEntity = entities.find((e) => e.name === "WBSETCL");
          return wbsetclEntity ? [wbsetclEntity.name] : [entities[0].name];
        } else if (prevSelected.length > 0) {
          // Filter out entities that are no longer available
          const validEntities = prevSelected.filter((entityName) =>
            entities.some((e) => e.name === entityName),
          );
          if (validEntities.length !== prevSelected.length) {
            return validEntities;
          }
        }
        return prevSelected;
      });
    } catch (error) {
      console.error("Error fetching available entities:", error);
    }
  }, [DefaultDates, activeTab]);

  const fetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      // Always use filters if selected - send comma-separated for multiple
      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? undefined // All selected, don't filter
          : selectedExchanges.length > 0
            ? selectedExchanges.join(",")
            : undefined;

      const productName =
        selectedProducts.length === products.length
          ? undefined // All selected, don't filter
          : selectedProducts.length > 0
            ? selectedProducts.join(",")
            : undefined;

      // Send comma-separated for multiple entities
      const entityName =
        selectedEntities.length > 0 ? selectedEntities.join(",") : undefined;

      const params: PowerExchangeAnalysisParams = {
        interval: selectedInterval.key,
        function: activefunctionTab as "comparative" | "cumulative",
        start_date: startDateStr,
        end_date: endDateStr,
      };

      if (exchangeName) {
        params.exchange_name = exchangeName;
      }
      if (productName) {
        params.product_name = productName;
      }
      if (entityName) {
        params.entity_name = entityName;
      }

      const response = await getPowerExchangeAnalysisData(params);

      setRawData(response.data);
      setSummaryNumbers(response.summary);

      if (selectedInterval.key === "slot") {
        setUniqueBlock(
          Array.from(
            new Set(
              response.data.map(
                (item: PowerExchangeAnalysisData) => item.x_axis,
              ),
            ),
          ),
        );
      } else {
        setUniqueBlock([]);
      }
    } catch (error) {
      console.error("Error fetching power exchange analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      dispatch(setLoadingFilterName(""));
    }
  }, [
    DefaultDates,
    activeTab,
    selectedInterval.key,
    activefunctionTab,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
  ]);

  // Fetch entities when date range changes
  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      selectedInterval,
      selectedUnit,
      selectedBlock,
      selectedTypes,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      activefunctionTab,
    };
    savePersistedFilters(filters);
  }, [
    selectedInterval,
    selectedUnit,
    selectedBlock,
    selectedTypes,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
    activefunctionTab,
  ]);

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      selectedInterval,
      selectedUnit,
      selectedBlock,
      selectedTypes,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      activefunctionTab,
    };
    savePersistedFilters(filters);
  }, [
    selectedInterval,
    selectedUnit,
    selectedBlock,
    selectedTypes,
    selectedExchanges,
    selectedProducts,
    selectedEntities,
    activefunctionTab,
  ]);

  const processDataForInterval = useCallback(
    (
      sourceData: PowerExchangeAnalysisData[],
      intervalConfig: (typeof Intervals)[number],
    ) => {
      let filteredData = [...sourceData];

      // Apply filters - in cumulative mode, backend already filters, so skip frontend filtering
      // (cumulative mode returns null for exchange/entity/product fields)
      if (activefunctionTab === "comparative") {
        if (
          selectedExchanges.length > 0 &&
          selectedExchanges.length < exchanges.length
        ) {
          filteredData = filteredData.filter(
            (item) =>
              item.exchange__name &&
              selectedExchanges.includes(item.exchange__name),
          );
        }

        if (
          selectedProducts.length > 0 &&
          selectedProducts.length < products.length
        ) {
          filteredData = filteredData.filter(
            (item) =>
              item.product__name &&
              selectedProducts.includes(item.product__name),
          );
        }

        if (selectedEntities.length > 0) {
          filteredData = filteredData.filter(
            (item) =>
              item.entity__name && selectedEntities.includes(item.entity__name),
          );
        }
      }

      if (selectedBlock.length > 0 && intervalConfig.key === "slot") {
        filteredData = filteredData.filter((item) =>
          selectedBlock.map((block) => block.value).includes(item.x_axis),
        );
      }

      const groupedData: any = {};

      filteredData.forEach((item) => {
        let key: string;
        if (
          (intervalConfig.isTime ||
            intervalConfig.values.includes("second_x_axis")) &&
          item.second_x_axis
        ) {
          key = `${item.second_x_axis}_${item.x_axis}`;
        } else {
          key = item.x_axis;
        }

        if (!groupedData[key]) {
          groupedData[key] = {
            x_axis: item.x_axis,
            second_x_axis: item.second_x_axis,
          };
        }

        const typeName = item.type__name || "";
        const exchangeName = item.exchange__name || "";
        const entityName = item.entity__name || "";
        const productName = item.product__name || "";

        selectedVariableMetrics.forEach((typeMetric) => {
          let actualSuffix = typeMetric.suffix;
          if (typeMetric.unit === "MW/MWh") {
            actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
          }

          // For price: group only by exchange and product (not by type or entity)
          // For cost and amount: group by type, exchange, entity, product (or just type in cumulative)
          let prefix: string;
          if (typeMetric.suffix === "price") {
            // Price: only by exchange and product
            if (activefunctionTab === "cumulative") {
              prefix = ""; // Cumulative: single price line
            } else {
              prefix =
                exchangeName && productName
                  ? `${exchangeName}_${productName}`
                  : "";
            }
          } else {
            // Cost and Amount: by type, exchange, entity, product
            prefix =
              activefunctionTab === "cumulative"
                ? `${typeName}`
                : exchangeName && entityName && productName
                  ? `${typeName}_${exchangeName}_${entityName}_${productName}`
                  : `${typeName}`;
          }

          const dataKey = prefix ? `${prefix}_${actualSuffix}` : actualSuffix;

          if (typeMetric.suffix === "amount") {
            const value =
              selectedUnit === "MW" ? item.amount_mw : item.amount_mwh;
            groupedData[key][dataKey] = value;
          } else if (typeMetric.suffix === "cost") {
            // Convert cost to lakhs for y-axis display (1 lakh = 100,000)
            groupedData[key][dataKey] = item.cost / 100000;
            // Store original cost value for tooltip
            groupedData[key][`${dataKey}_original`] = item.cost;
          } else if (typeMetric.suffix === "price") {
            // Price is already in Rs/kWh, use as is
            // For price, we need to handle multiple types (Drawal/Injection) with same exchange/product
            // Since price is same for both, we can use any one
            if (!groupedData[key][dataKey]) {
              groupedData[key][dataKey] = item.wt_avg_price || 0;
            }
          }
        });
      });

      const formattedChartData = Object.values(groupedData);

      // Generate keys based on all selected metrics
      // For price: keys are exchange_product (or empty for cumulative)
      // For cost/amount: keys are type_exchange_entity_product (or type for cumulative)
      const allKeys = new Set<string>();

      selectedVariableMetrics.forEach((typeMetric) => {
        if (typeMetric.suffix === "price") {
          // Price keys: exchange_product (or empty for cumulative)
          if (activefunctionTab === "cumulative") {
            allKeys.add(""); // Single key for cumulative price
          } else {
            filteredData
              .filter((item) => item.exchange__name && item.product__name)
              .forEach((item) => {
                allKeys.add(`${item.exchange__name}_${item.product__name}`);
              });
          }
        } else {
          // Cost and Amount keys: type_exchange_entity_product (or type for cumulative)
          if (activefunctionTab === "cumulative") {
            filteredData.forEach((item) => {
              if (item.type__name) {
                allKeys.add(item.type__name);
              }
            });
          } else {
            filteredData.forEach((item) => {
              const typeName = item.type__name || "";
              const exchangeName = item.exchange__name || "";
              const entityName = item.entity__name || "";
              const productName = item.product__name || "";
              if (exchangeName && entityName && productName) {
                allKeys.add(
                  `${typeName}_${exchangeName}_${entityName}_${productName}`,
                );
              } else if (typeName) {
                allKeys.add(typeName);
              }
            });
          }
        }
      });

      const keys = Array.from(allKeys);

      return {
        chartData: formattedChartData,
        tableData: filteredData,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      selectedBlock,
      selectedExchanges,
      selectedProducts,
      selectedEntities,
      selectedTypes,
      selectedVariableMetrics,
      selectedUnit,
    ],
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSummaryIntervalData([]);
      return;
    }

    const processed = processDataForInterval(rawData, selectedInterval);
    setChartData(processed.chartData);
    setChartKeys(processed.chartKeys);
    setTableData(processed.tableData);
    setSummaryIntervalData(processed.chartData);
  }, [rawData, selectedInterval, processDataForInterval]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string,
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    const dataKey = key ? `${key}_${suffix}` : suffix;
    const legendName = `${normalizeDisplayLabel(key)} ${displayName}`;

    return {
      dataKey,
      name: legendName,
      type: chartType,
      unit: actualUnit,
      yAxisId: yAxisId,
      stackId:
        key && key.includes("Drawal")
          ? "drawal"
          : key && key.includes("Injection")
            ? "injection"
            : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      // Extract type name from key (key format: "TypeName", "TypeName_Exchange_Entity_Product", or "Exchange_Product")
      const keyType = key.split("_")[0];
      const isTypeKey = ["Drawal", "Injection"].includes(keyType);

      // Filter by selected types - only include legends for selected types
      // Non-type keys (like Exchange_Product for price) will skip this check
      if (
        isTypeKey &&
        selectedTypes.length > 0 &&
        !selectedTypes.includes(keyType)
      ) {
        return; // Skip this key if its type is not selected
      }

      selectedVariableMetrics.forEach((typeMetric) => {
        let actualSuffix = typeMetric.suffix;
        if (typeMetric.unit === "MW/MWh") {
          actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        // Calculate the dataKey that would be used
        const dataKey = key ? `${key}_${actualSuffix}` : actualSuffix;

        // Check if this dataKey exists in chartData (has actual data)
        const hasData = chartData.some((dataPoint) => {
          const value = dataPoint[dataKey];
          return (
            value !== undefined && value !== null && !isNaN(parseFloat(value))
          );
        });

        // Only create legend if data exists
        if (hasData) {
          legends.push(
            createChartObject(
              key,
              index,
              actualSuffix,
              typeMetric.displayName,
              typeMetric.stackSuffix,
              typeMetric.chartType,
              typeMetric.unit,
              typeMetric.yAxisId,
            ),
          );
        }
      });
    });

    return legends;
  }

  const memoizedLegends = useMemo(
    () => getLegends(),
    [
      chartKeys,
      selectedVariableMetrics,
      selectedUnit,
      selectedTypes,
      chartData,
    ],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(memoizedLegends);

  const costLegends = useMemo(
    () =>
      visibleLegends.map((legend: LegendKey) =>
        legend.unit === "Rs" ? legend : null,
      ),
    [visibleLegends],
  );

  // Use "Rs(Lakhs)" for cost/price display in Summary table
  const summaryLegends = useMemo(
    () =>
      visibleLegends.map((legend: LegendKey) =>
        legend.unit === "Rs" ? { ...legend, unit: "Rs(Lakhs)" } : legend,
      ),
    [visibleLegends],
  );

  const xDataKey = "x_axis";

  const viewExtraKeys = selectedInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const viewExtraLabels = selectedInterval.isTime
    ? ["Time Slot", selectedInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const summaryExtraKeys = selectedInterval.isTime
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const summaryExtraLabels = selectedInterval.isTime
    ? ["Time Slot", selectedInterval.key === "slot" ? "Date" : "Month"]
    : ["Date"];

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    tableUnits[curr.suffix] = actualUnit;
  });

  const tablePar = {
    legends: summaryLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: viewExtraKeys,
    extraLabels: viewExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Entity Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate,
    }),
    customUnits: tableUnits,
    showTotal: false,
  };

  function secondDataKey() {
    if (selectedInterval.values.includes("second_x_axis")) {
      return "second_x_axis";
    } else {
      return undefined;
    }
  }

  return (
    <>
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
                setSelectedBlock([]);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />

            <FilterMenu
              title="Function"
              items={DataTabs.map(
                (tab) => tab.charAt(0).toUpperCase() + tab.slice(1),
              )}
              selectedItems={[
                activefunctionTab.charAt(0).toUpperCase() +
                  activefunctionTab.slice(1),
              ]}
              onSelect={(item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />

            <FilterMenu
              title="Exchange"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={(items: string[]) => {
                setSelectedExchanges(items);
              }}
            />

            <FilterMenu
              title="Product"
              items={products}
              selectedItems={selectedProducts}
              setSelectedItems={(items: string[]) => {
                setSelectedProducts(items);
              }}
            />

            <FilterMenu
              title="Type"
              items={typeFilters.map((item) => item.label)}
              selectedItems={selectedTypes}
              setSelectedItems={(items: string[]) => {
                setSelectedTypes(items);
              }}
            />

            <FilterMenu
              title="Variable"
              items={variableFilterMetrics.map((item) => item.displayName)}
              selectedItems={selectedVariableMetrics
                .filter((m) => m.suffix !== "amount")
                .map((item) => item.displayName)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                // Only allow one selection at a time (Cost or Price)
                // If a new item is selected, replace the previous one
                let newSelectedMetric;
                if (selectedItems.length > 0) {
                  // Get the last selected item (most recent selection)
                  const lastSelected = selectedItems[selectedItems.length - 1];
                  newSelectedMetric = variableFilterMetrics.find(
                    (item) => item.displayName === lastSelected,
                  );
                } else {
                  // If nothing selected, default to Cost
                  newSelectedMetric = variableFilterMetrics[0];
                }

                // Always include volume metric + the selected Cost/Price
                if (newSelectedMetric) {
                  setSelectedVariableMetrics([volumeMetric, newSelectedMetric]);
                }
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          {/* Summary Numbers */}
          {(() => {
            const getFormattedCost = (val: number) => {
              if (!val) return { value: 0, unit: "₹" };
              const absValue = Math.abs(val);
              if (absValue >= 10000000)
                return { value: val / 10000000, unit: "₹ Cr" };
              if (absValue >= 100000)
                return { value: val / 100000, unit: "₹ Lakhs" };
              if (absValue >= 1000) return { value: val / 1000, unit: "₹ K" };
              return { value: val, unit: "₹" };
            };

            const dCost = getFormattedCost(summaryNumbers.total_drawal_cost);
            const iCost = getFormattedCost(summaryNumbers.total_injection_cost);

            return (
              <div className="flex flex-wrap gap-4 my-0 justify-center">
                <MetricChip
                  icon={
                    <div className="bg-orange-100 p-1 rounded-full">
                      <Wallet className="w-4 h-4 text-orange-600" />
                    </div>
                  }
                  items={[
                    {
                      label: "Total Drawal Cost",
                      value: loading ? null : dCost.value,
                      unit: dCost.unit,
                    },
                  ]}
                />
                <MetricChip
                  icon={
                    <div className="bg-purple-100 p-1 rounded-full">
                      <BarChart3 className="w-4 h-4 text-purple-600" />
                    </div>
                  }
                  items={[
                    {
                      label: "Total Drawal Volume",
                      value: loading
                        ? null
                        : summaryNumbers.total_drawal_volume_mu,
                      unit: "MU",
                    },
                  ]}
                />
                <MetricChip
                  icon={
                    <div className="bg-green-100 p-1 rounded-full">
                      <Wallet className="w-4 h-4 text-green-600" />
                    </div>
                  }
                  items={[
                    {
                      label: "Total Injection Cost",
                      value: loading ? null : iCost.value,
                      unit: iCost.unit,
                    },
                  ]}
                />
                <MetricChip
                  icon={
                    <div className="bg-blue-100 p-1 rounded-full">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                    </div>
                  }
                  items={[
                    {
                      label: "Total Injection Volume",
                      value: loading
                        ? null
                        : summaryNumbers.total_injection_volume_mu,
                      unit: "MU",
                    },
                  ]}
                />
              </div>
            );
          })()}
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader
                title="Loading Power Exchange Analysis"
                subtitle="Fetching power exchange entity analysis data…"
              />
            ) : (
              <>
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">Power Exchange Analysis</h2>
                  <div className="flex gap-x-6 justify-end items-center">
                    <div className="w-80">
                      {/* <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity
                  </label> */}
                      <SearchBox
                        options={availableEntities.map((e) => ({
                          value: e.name,
                          label: e.name,
                        }))}
                        selectedOptions={selectedEntities.map((name) => ({
                          value: name,
                          label: name,
                        }))}
                        onChange={(selected: any) => {
                          if (Array.isArray(selected)) {
                            setSelectedEntities(
                              selected.map(
                                (option: any) => option.value as string,
                              ),
                            );
                          } else {
                            setSelectedEntities([]);
                          }
                        }}
                        placeholder="Select Entity"
                        isMany={true}
                      />
                    </div>
                    {selectedInterval.key === "slot" && (
                      <BlockRangeSelector
                        blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                        placeholder="Select Slot"
                        selectedOptions={selectedBlock.map(
                          (item) => item.value,
                        )}
                        setSelectedOptions={setSelectedBlock}
                      />
                    )}
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.Start}
                    isTimeSlot={selectedInterval.isTime}
                    onlyTitle={true}
                    legends={memoizedLegends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    // secondYAxisDomain={[0, 30000000]}
                    secondYAxisLabel={
                      selectedVariableMetrics.some((m) => m.suffix === "price")
                        ? "₹/kWh"
                        : selectedVariableMetrics.some(
                              (m) => m.suffix === "cost",
                            )
                          ? "₹ (Lakhs)"
                          : undefined
                    }
                    yAxisLabel={selectedUnit}
                    xDataKey={xDataKey}
                    xTick={
                      selectedInterval.key === "slot" &&
                      selectedBlock.length === 0
                        ? (value: any, index: any) => {
                            return renderHourTick(value, 10);
                          }
                        : selectedInterval.key === "week"
                          ? renderQuarterTick
                          : undefined
                    }
                    secondXDataKey={secondDataKey()}
                    customTooltip={(params: any) => {
                      const param = Array.isArray(params) ? params[0] : params;
                      let items = Array.isArray(params) ? params : [params];

                      let title = param.name;
                      if (
                        secondDataKey() &&
                        chartData[param.dataIndex]?.[secondDataKey()!]
                      ) {
                        if (selectedInterval.isTime) {
                          title = `${chartData[param.dataIndex][secondDataKey()!]} - ${
                            param.name
                          } (${getTimeRange(parseInt(param.name))})`;
                        } else {
                          title = `${chartData[param.dataIndex][secondDataKey()!]} - ${
                            param.name
                          }`;
                        }
                      } else if (selectedInterval.isTime) {
                        title =
                          getTimeRange(parseInt(param.name)) +
                          ` (${param.name})`;
                      }

                      const dataPoint = chartData[param.dataIndex];

                      const tooltipItems = items
                        .filter((item: any) => {
                          const value = parseFloat(item.value);
                          return !isNaN(value) && isFinite(value);
                        })
                        .map((item: any) => {
                          // Find the legend to get the dataKey
                          const legend = memoizedLegends.find(
                            (l) => l.name === item.seriesName,
                          );
                          const dataKey = legend?.dataKey || "";

                          // Check if this is a cost value and get original value
                          let displayValue: number = parseFloat(item.value);
                          if (dataKey.includes("cost") && dataPoint) {
                            // Try to find the original value
                            const originalKey = `${dataKey}_original`;

                            if (originalKey in dataPoint) {
                              displayValue = dataPoint[originalKey];
                            } else {
                              // Convert back from lakhs to original (value shown is in lakhs)
                              displayValue = displayValue * 100000;
                            }
                          }
                          // Price is already in Rs/kWh, no conversion needed

                          // Format based on unit
                          let formattedValue: string;
                          if (legend?.unit === "Rs") {
                            formattedValue = formatCostTooltip(displayValue);
                          } else if (legend?.unit === "Rs/kWh") {
                            // Price: show with 2 decimal places
                            formattedValue = displayValue
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                          } else {
                            formattedValue = displayValue
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                          }

                          const bgColor = item.color;
                          const unit = legend?.unit || "";
                          const displaySeriesName = item.seriesName;

                          return `
                        <div class="tooltip-item" data-name="${displaySeriesName?.toLowerCase()}">
                          <div class="color-indicator" style="background-color: ${bgColor}"></div>
                          <div class="series-name">${displaySeriesName}</div>
                          <div class="value">${formattedValue}${unit ? ` ${unit.replace("Rs", "").replace("/kWh", "₹/kWh")}` : ""}</div>
                        </div>
                      `;
                        });

                      const tooltipId = `tooltip-${Date.now()}`;
                      const fontSize = 12;

                      return `
                    <div class="enhanced-tooltip" id="${tooltipId}">
                      <div class="tooltip-header">
                        <div class="tooltip-title">${title}</div>
                      </div>
                      
                      <div class="tooltip-content">
                        ${tooltipItems.join("")}
                      </div>
                    </div>
                    
                    <style>
                      .enhanced-tooltip {
                        font-family: 'Inter', system-ui, sans-serif;
                        width: 100%;
                        min-width: 200px;
                      }
                      .tooltip-header {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        justify-content: start;
                        align-items: start;
                        border-bottom: 1px solid #eaeaea;
                        padding-bottom: 8px;
                        margin-bottom: 8px;
                      }
                      .tooltip-title {
                        font-weight: 600;
                        font-size: ${fontSize + 1}px;
                        color: #333;
                      }
                      .tooltip-content {
                        max-height: 300px;
                        overflow-y: auto;
                        padding-right: 10px;
                      }
                      .tooltip-item {
                        display: flex;
                        align-items: center;
                        padding: 4px 0;
                        border-bottom: 1px solid #f0f0f0;
                      }
                      .tooltip-item:last-child {
                        border-bottom: none;
                      }
                      .color-indicator {
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        margin-right: 8px;
                      }
                      .series-name {
                        flex: 1;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-size: ${fontSize}px;
                      }
                      .value {
                        font-weight: 500;
                        margin-left: 10px;
                        font-size: ${fontSize}px;
                      }
                    </style>
                  `;
                    }}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummarySlotTable
                    legends={summaryLegends}
                    avgData={chartData}
                    minMaxData={
                      summaryIntervalData.length > 0
                        ? summaryIntervalData
                        : chartData
                    }
                    avgTableData={TableData}
                    extraKeys={summaryExtraKeys}
                    extraLabels={summaryExtraLabels}
                    unit={selectedUnit}
                    priceLegends={costLegends?.map((l) =>
                      l ? { ...l, unit: "Rs (Lakhs)" } : null,
                    )}
                    volumeUnitKey={selectedUnit.toLowerCase()}
                    block_key={
                      selectedInterval.key === "slot" ? "x_axis" : undefined
                    }
                    showWtdAvg={false}
                    showTotal={false}
                  />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>
          <div className="gna-footer-container grid grid-cols-3 items-center justify-between gap-5">
            <p className="text-[10px] text-gray-500 text-start leading-tight">
              * 2 Paisa per kWh is added as transaction fees for calculating
              cost.
            </p>
            <p className="gna-footer-text text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text text-end">Beta Version</strong>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default PowerExchangeAnalysis;
